import { createClient } from '@supabase/supabase-js';
import clientEnv from '../env';
// Use validated environment variables from env.ts
const supabaseUrl = clientEnv.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = clientEnv.REACT_APP_SUPABASE_ANON_KEY;
// Validate environment variables with detailed error message
if (!supabaseUrl || !supabaseAnonKey) {
    const missing = [];
    if (!supabaseUrl)
        missing.push('REACT_APP_SUPABASE_URL');
    if (!supabaseAnonKey)
        missing.push('REACT_APP_SUPABASE_ANON_KEY');
    const errorMessage = `Missing required Supabase environment variables: ${missing.join(', ')}. ` +
        `These must be set in Cloudflare Pages → Settings → Environment Variables and the build must be retriggered. ` +
        `Current NODE_ENV: ${clientEnv.NODE_ENV || 'undefined'}. ` +
        `Variables were not injected during build time.`;
    console.error('❌ Supabase Configuration Error:', {
        missing,
        NODE_ENV: clientEnv.NODE_ENV,
        hasSupabaseUrl: !!supabaseUrl,
        hasSupabaseAnonKey: !!supabaseAnonKey,
        allEnvKeys: Object.keys(clientEnv).filter(k => k.startsWith('REACT_APP_'))
    });
    throw new Error(errorMessage);
}
// Get the environment
const isProduction = clientEnv.NODE_ENV === 'production';
// Create Supabase client with explicit configuration
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
        persistSession: true,
        autoRefreshToken: true,
        storageKey: 'supabase_auth_token',
        storage: localStorage,
        flowType: 'pkce',
        debug: clientEnv.NODE_ENV === 'development',
        lock: async (_name, _acquireTimeout, fn) => fn(),
    },
    global: {
        headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey,
            // Accept all content types to prevent 406 errors
            'Accept': '*/*',
            // Enable proper return format for PostgREST
            'Prefer': 'return=representation'
        },
    },
    realtime: {
        params: {
            eventsPerSecond: 10
        }
    }
});
// Set email sender details globally for any resend or signup operation
supabase.auth.onAuthStateChange((event, session) => {
    // This will be used when sending any auth emails
    const defaultOptions = {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        email: {
            senderName: 'My Pace Support',
            senderEmail: 'customer.service@mypace.app',
        }
    };
    // Store this in localStorage to be used for all auth operations
    localStorage.setItem('supabase_auth_options', JSON.stringify(defaultOptions));
    if (session) {
        // Store tokens in localStorage for backward compatibility
        localStorage.setItem('access_token', session.access_token);
        localStorage.setItem('refresh_token', session.refresh_token);
    }
    else if (event === 'SIGNED_OUT') {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
    }
});
// Add a helper function to get the default auth options
export const getAuthOptions = () => {
    return {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: {
            email_sender: 'customer.service@mypace.app',
            email_sender_name: 'My Pace Support'
        }
    };
};
// Update getSession to use an enhanced function with retry capability
const getSessionWithRetry = async (retries = 3) => {
    let attempt = 0;
    while (attempt < retries) {
        try {
            const { data, error } = await supabase.auth.getSession();
            if (error)
                throw error;
            return { data, error: null };
        }
        catch (err) {
            attempt++;
            console.error(`Session retrieval attempt ${attempt} failed:`, err);
            if (attempt >= retries)
                return { data: null, error: err };
            // Wait before retrying, with exponential backoff
            await new Promise(r => setTimeout(r, 500 * Math.pow(2, attempt)));
        }
    }
    return { data: null, error: new Error('Failed to get session after multiple attempts') };
};
/**
 * Timeout-safe getSession.
 *
 * Supabase JS v2 wraps `getSession` in `navigator.locks` which can deadlock if
 * any other tab / worker is holding the same lock (or if a previous getSession
 * promise was abandoned mid-flight). When that happens every caller — axios
 * interceptor, ProfileInitializer, PersistentDataProvider, socket-utils — hangs
 * forever and no API requests are ever issued (cause of the "data only loads
 * after refresh" bug).
 *
 * This helper:
 *   1. Races getSession() against a 3s timeout.
 *   2. Falls back to localStorage-stored tokens (already kept in sync by
 *      onAuthStateChange in this module) if the lock is wedged or no session.
 *   3. De-duplicates concurrent callers via a shared in-flight promise so we
 *      issue at most ONE getSession() per ~200ms window even when many
 *      components mount in parallel.
 *
 * Returns a session-shape object (or null). NEVER throws — wedged auth must
 * never propagate as an exception that prevents the app from rendering.
 */
const SESSION_TIMEOUT_MS = 3000;
const SESSION_DEDUPE_MS = 200;
let inflightSessionPromise = null;
let inflightSessionAt = 0;
function readSessionFromLocalStorage() {
    try {
        if (typeof localStorage === 'undefined')
            return null;
        const access_token = localStorage.getItem('access_token');
        const refresh_token = localStorage.getItem('refresh_token');
        if (access_token && access_token.trim() !== '') {
            return { access_token, refresh_token: refresh_token ?? '' };
        }
        return null;
    }
    catch {
        return null;
    }
}
export async function getSessionSafe(timeoutMs = SESSION_TIMEOUT_MS) {
    const now = Date.now();
    if (inflightSessionPromise && now - inflightSessionAt < SESSION_DEDUPE_MS) {
        return inflightSessionPromise;
    }
    inflightSessionAt = now;
    inflightSessionPromise = (async () => {
        let timeoutHandle = null;
        try {
            const live = supabase.auth.getSession().then(({ data, error }) => {
                if (error)
                    throw error;
                const s = data?.session;
                if (!s?.access_token)
                    return null;
                return { access_token: s.access_token, refresh_token: s.refresh_token ?? '' };
            });
            const timeout = new Promise((resolve) => {
                timeoutHandle = setTimeout(() => {
                    console.warn(`[supabase-client] getSession() exceeded ${timeoutMs}ms — falling back to localStorage tokens. ` +
                        'Likely cause: navigator.locks contention (Supabase JS v2). ' +
                        'App will continue with cached tokens until lock is released.');
                    resolve(null);
                }, timeoutMs);
            });
            const result = await Promise.race([live, timeout]);
            if (result)
                return result;
            // Timeout or no session → fall back to localStorage.
            return readSessionFromLocalStorage();
        }
        catch (err) {
            console.warn('[supabase-client] getSession() threw, falling back to localStorage:', err);
            return readSessionFromLocalStorage();
        }
        finally {
            if (timeoutHandle !== null)
                clearTimeout(timeoutHandle);
            // Release the de-dupe slot shortly after resolution so we don't pin a
            // stale result for the dedupe window when callers actually want fresh.
            setTimeout(() => {
                inflightSessionPromise = null;
            }, SESSION_DEDUPE_MS);
        }
    })();
    return inflightSessionPromise;
}
// NOTE: We intentionally do NOT call getSession() at module load anymore.
// On every page boot multiple components (ProfileInitializer,
// PersistentDataProvider, axios interceptor, socket-utils) already call
// getSessionSafe(), and adding another module-level getSession() multiplied
// navigator.locks contention which made the lock wedge happen on every app
// load. ProfileInitializer is the single source of truth for "app boot
// session warm-up".
// Check for required tables
const checkTable = async (tableName) => {
    try {
        // Use a simpler query that's less likely to trigger RLS recursion
        const { data, error } = await supabase
            .from(tableName)
            .select('id')
            .limit(1)
            .maybeSingle();
        if (error) {
            // Check for recursion errors and just log them without failing
            if (error.message && error.message.includes('recursion')) {
                console.warn(`⚠️ RLS recursion detected in ${tableName} - this is expected if policies reference each other`);
                return true; // Continue despite error
            }
            console.error(`Error accessing ${tableName} table:`, error.message || error);
            return false;
        }
        return true;
    }
    catch (err) {
        // Catch all errors and just warn about them
        console.warn(`⚠️ Could not check ${tableName} table: ${err.message || err}`);
        return true; // Continue despite error
    }
};
// Check required tables on init with error handling
const checkRequiredTables = async () => {
    try {
        // These are the core tables we need to verify
        // Wrap each in try/catch to prevent one failure from stopping others
        let results = {
            notesExists: false,
            studentsExists: false,
            tutorsExists: false
        };
        try {
            results.notesExists = await checkTable('notes');
        }
        catch (e) {
            console.warn('Failed to check notes table');
        }
        try {
            results.studentsExists = await checkTable('students');
        }
        catch (e) {
            console.warn('Failed to check students table');
        }
        try {
            results.tutorsExists = await checkTable('tutors');
        }
        catch (e) {
            console.warn('Failed to check tutors table');
        }
        return results;
    }
    catch (error) {
        console.error('Error checking required tables:', error.message || error);
        // Return default values but don't fail
        return {
            notesExists: false,
            studentsExists: false,
            tutorsExists: false
        };
    }
};
// Test a simple direct Supabase query to see if we can access the database at all
// Uses tutors table instead of notes to avoid unnecessary queries on a frequently-used table
const testDatabaseConnection = async () => {
    try {
        // Use tutors table instead of notes to avoid unnecessary queries
        // This is a less frequently accessed table and won't clutter logs
        const { data, error } = await supabase
            .from('tutors')
            .select('id')
            .limit(1);
        if (error) {
            console.error('Database connection test failed:', error);
            return false;
        }
        return true;
    }
    catch (err) {
        console.error('Database connection test failed:', err);
        return false;
    }
};
// NOTE: We intentionally do NOT run checkRequiredTables() or
// testDatabaseConnection() at module load. They fired 4 Supabase queries on
// every app boot before the auth interceptor was even wired up, contributing
// to navigator.locks contention and rate-limit pressure on cold starts. They
// are kept exported for opt-in diagnostics only.
// To run them on demand (e.g. from a dev tool):
//   import { runInitialDiagnostics } from './supabase-client';
//   runInitialDiagnostics();
export const runInitialDiagnostics = async () => {
    const tables = await checkRequiredTables();
    const dbOk = await testDatabaseConnection();
    return { tables, dbOk };
};
/**
 * Get a user by ID from the appropriate table
 * @param userId - The user's ID
 * @returns The user data or null if not found
 */
export const getUserById = async (userId) => {
    if (!userId) {
        console.error('getUserById: No userId provided');
        return null;
    }
    try {
        // Try students table first (guaranteed to exist from migration)
        let { data, error } = await supabase
            .from('students')
            .select('*')
            .eq('id', userId)
            .maybeSingle();
        if (data && !error) {
            return { ...data, source: 'students' };
        }
        if (error)
            console.error('Error fetching from students:', error.message);
        // Try tutors table
        ({ data, error } = await supabase
            .from('tutors')
            .select('*')
            .eq('id', userId)
            .maybeSingle());
        if (data && !error) {
            return { ...data, source: 'tutors' };
        }
        if (error)
            console.error('Error fetching from tutors:', error.message);
        // User not found in any table
        console.warn(`User with ID ${userId} not found in any table`);
        return null;
    }
    catch (err) {
        console.error('Error in getUserById:', err);
        return null;
    }
};
// Safe function to get user profile that doesn't depend on RLS
export const getUserProfileSafe = async (userId, userRole) => {
    if (!userId)
        return null;
    try {
        // First try using the RPC function that bypasses RLS
        const { data: rpcData, error: rpcError } = await supabase
            .rpc('get_user_profile_by_id', {
            user_id: userId,
            user_role: userRole
        });
        if (!rpcError && rpcData) {
            return rpcData;
        }
        if (rpcError) {
            console.error(`RPC function error: ${rpcError.message}. Cannot retrieve profile.`);
            // Last resort: Return minimal profile with just the ID and role
            return { id: userId, role: userRole };
        }
        // If no data returned from RPC, return minimal profile
        if (!rpcData) {
            console.warn(`No data returned from RPC for ${userRole} profile`);
            return { id: userId, role: userRole };
        }
        return rpcData;
    }
    catch (err) {
        console.error(`Failed to get ${userRole} profile:`, err.message || err);
        return { id: userId, role: userRole };
    }
};
export default supabase;
